import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  HelpCircle,
  Book,
  Mail,
  MessageCircle,
  Settings,
  LayoutDashboard,
  Calendar,
  Clock,
  Building2,
  Wrench,
  CheckSquare,
  Printer,
  Users,
  Send,
  Phone,
  Globe
} from 'lucide-react';

function Support({ user }) {
  const { t } = useLanguage();
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: '',
    priority: 'Medium'
  });
  const [sending, setSending] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subject: emailForm.subject,
          message: emailForm.message,
          priority: emailForm.priority,
          userEmail: user.email,
          username: user.username
        })
      });

      if (response.ok) {
        toast.success('Support request sent successfully! We\'ll get back to you within 24 hours.');
        setEmailForm({ subject: '', message: '', priority: 'Medium' });
      } else {
        throw new Error('Failed to send support request');
      }
    } catch (error) {
      console.error('Support request error:', error);
      toast.error('Failed to send support request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const userManualSections = [
    {
      id: 'getting-started',
      title: t('gettingStartedTitle'),
      icon: Settings,
      content: [
        t('welcomeToComposysMaintPro'),
        t('afterLoggingIn'),
        t('your14DayFreeTrial')
      ]
    },
    {
      id: 'repair-work-orders',
      title: t('repairWorkOrdersTitle'),
      icon: Wrench,
      content: [
        t('viewRepairWorkOrders'),
        t('clickAnyWorkOrder'),
        t('dragAndDropWorkOrders'),
        t('useSearchFilter'),
        t('clickNewWorkOrder'),
        t('editChecklistsBy')
      ]
    },
    {
      id: 'maintenance-work-orders',
      title: t('maintenanceWorkOrdersTitle'),
      icon: Calendar,
      content: [
        t('schedulePreventive'),
        t('clickOnMaintenance'),
        t('eachMaintenanceTask'),
        t('clickOnAnyMaintenance'),
        t('printMaintenanceWork'),
        t('trackCompletionProgress')
      ]
    },
    {
      id: 'calendar-view',
      title: 'Calendar & Scheduling',
      icon: Calendar,
      content: [
        '• View all scheduled maintenance and repair tasks in calendar format',
        '• Navigate between months to see future and past schedules',
        '• Click on any date to see tasks scheduled for that day',
        '• Click on task cards in the right panel to view full details',
        '• Color coding: Green for maintenance, Blue for repair work orders'
      ]
    },
    {
      id: 'daily-tasks',
      title: 'Daily Tasks Dashboard',
      icon: Clock,
      content: [
        '• See today\'s tasks with status summary: Overdue, Pending, In Progress, Completed',
        '• "Urgent & Upcoming" section shows overdue and tasks starting within 2 hours',
        '• Click on any task card to view details and make updates',
        '• Real-time clock shows current time for scheduling reference',
        '• Visual indicators for overdue tasks and upcoming deadlines'
      ]
    },
    {
      id: 'departments-machines',
      title: 'Departments & Machines',
      icon: Building2,
      content: [
        '• Organize your facility with departments (Admin only)',
        '• Add machines to each department for precise tracking',
        '• Click on department cards to see all machines in that department',
        '• Click on machine cards to view specifications, notes, and maintenance history',
        '• Upload manuals, diagrams, and documentation for each machine',
        '• Create machine-specific maintenance checklists'
      ]
    },
    {
      id: 'checklists-printing',
      title: 'Checklists & Printing',
      icon: CheckSquare,
      content: [
        '• Create detailed checklists for both maintenance and repair tasks',
        '• Check off completed items with real-time progress tracking',
        '• Add or remove checklist items as needed',
        '• Print professional work orders with checklists for field technicians',
        '• Printed orders include signature lines and space for notes',
        '• All checklists show completion percentages on cards'
      ]
    },
    {
      id: 'user-roles',
      title: 'User Roles & Permissions',
      icon: Users,
      content: [
        '• Admin: Full access to create departments, machines, and all work orders',
        '• Maintenance Supervisor: Can create and manage work orders and maintenance tasks',
        '• Both roles can print work orders and manage daily operations',
        '• Role-based menu items ensure users see only relevant features'
      ]
    }
  ];

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              <HelpCircle className="w-6 h-6 mr-2 text-blue-600" />
              {t('supportHelpCenter')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('supportDescription')}
            </p>
          </div>
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            Version 1.0
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="manual" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="manual" className="flex items-center space-x-2">
            <Book className="w-4 h-4" />
            <span>{t('userManual')}</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>{t('contactDeveloper')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Book className="w-5 h-5 mr-2 text-green-600" />
                Composys MaintPro User Manual
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {userManualSections.map((section) => {
                const Icon = section.icon;
                return (
                  <div key={section.id} className="space-y-3">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Icon className="w-5 h-5 mr-2 text-blue-600" />
                      {section.title}
                    </h3>
                    <div className="pl-7 space-y-2">
                      {section.content.map((item, index) => (
                        <p key={index} className="text-gray-700 text-sm leading-relaxed">
                          {item}
                        </p>
                      ))}
                    </div>
                    {section.id !== userManualSections[userManualSections.length - 1].id && (
                      <Separator className="mt-4" />
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Form */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Mail className="w-5 h-5 mr-2 text-red-600" />
                  Contact Developer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email-subject">{t('subjectRequired')}</Label>
                    <Input
                      id="email-subject"
                      type="text"
                      placeholder={t('briefDescription')}
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email-priority">{t('priority')}</Label>
                    <select
                      id="email-priority"
                      value={emailForm.priority}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, priority: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Low">{t('low')} - {t('generalQuestions')}</option>
                      <option value="Medium">{t('medium')} - {t('featureRequests')}</option>
                      <option value="High">{t('high')} - {t('bugReports')}</option>
                      <option value="Critical">{t('critical')} - System down</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="email-message">{t('messageRequired')}</Label>
                    <Textarea
                      id="email-message"
                      placeholder={t('describeIssue')}
                      value={emailForm.message}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                      required
                      rows={6}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sending || !emailForm.subject.trim() || !emailForm.message.trim()}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    {sending ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{t('sending')}...</span>
                      </div>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t('sendSupportRequest')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Globe className="w-5 h-5 mr-2 text-purple-600" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-900 mb-3">Get in Touch</h4>
                  <div className="space-y-3 text-sm text-green-800">
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <a href="mailto:sales@composys.ca" className="hover:underline">
                        sales@composys.ca
                      </a>
                    </div>
                  </div>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    Composys MaintPro is actively developed and maintained
                  </p>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    Last updated: October 2025
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Support;